import type { MDXEditorMethods } from "@mdxeditor/editor";
import * as Popover from "@radix-ui/react-popover";
import {
  createContext,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import { geminiRequest } from "senf-shared";
import i18n from "senf-shared/utils/i18n";
import { cleanAndParseAiJson } from "senf-shared/utils/reinsertionHelpers";
import { GlobalStyle, Icon, useOnClickOutside } from "../../..";
import { useThemeColor } from "../../../hooks";
import { cn } from "../../../util/classNameConstructor";
import { Z_INDEX } from "../../../util/zIndexLayers";
import Button from "../button/Button";
import { Input } from "../inputs/Input";
import { diffToMarkdown } from "./diffStrings";

// Store persistent state per editor instance (keyed by ref object identity)
// This allows state to persist across component unmounts/remounts
type EditorState = {
  isOpen: boolean;
  previewValue: string | undefined;
  prompt: string;
  loading: boolean;
  isCancelled: boolean;
  initialValue: string;
  generatedValue: { value: string; diff: string };
};

// Use WeakMap to store state keyed by ref object (automatically cleaned up when ref is GC'd)
const editorStateMap = new WeakMap<
  RefObject<MDXEditorMethods | null>,
  EditorState
>();

// Helper to get or create state for an editor instance
const getOrCreateEditorState = (
  mdxEditorRef: RefObject<MDXEditorMethods | null>,
  initialValue: string,
): EditorState => {
  let state = editorStateMap.get(mdxEditorRef);
  if (!state) {
    state = {
      isOpen: false,
      previewValue: undefined,
      prompt: "",
      loading: false,
      isCancelled: false,
      initialValue,
      generatedValue: { value: "", diff: "" },
    };
    editorStateMap.set(mdxEditorRef, state);
  }
  return state;
};

// Context for sharing state within a component tree
export type TextEnhancementContextType = {
  isOpen: boolean;
  setIsOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
  previewValue: string | undefined;
  setPreviewValue: (value: string | undefined) => void;
  prompt: string;
  setPrompt: (value: string) => void;
  loading: boolean;
  setLoading: (value: boolean) => void;
  isCancelled: React.RefObject<boolean>;
  initialValue: React.RefObject<string>;
  generatedValue: React.RefObject<{ value: string; diff: string }>;
  handleAccept: () => void;
  handleReject: () => void;
  togglePreview: () => void;
  reset: () => void;
  mdxEditorRef: RefObject<MDXEditorMethods | null>;
  onChange: (value: string) => void;
  /** When set (e.g. translation column locale), Gemini uses this instead of the UI language */
  aiResponseLang: string | undefined;
};

const TextEnhancementContext = createContext<
  TextEnhancementContextType | undefined
>(undefined);

// Hook to access the context (must be used within provider)
export const useTextEnhancementContext = () => {
  const context = useContext(TextEnhancementContext);
  if (!context) {
    throw new Error(
      "useTextEnhancementContext must be used within TextEnhancementProvider",
    );
  }
  return context;
};

// Provider component that manages state per editor instance
export const TextEnhancementProvider = ({
  mdxEditorRef,
  onChange,
  value,
  aiResponseLang,
  children,
}: {
  mdxEditorRef: RefObject<MDXEditorMethods | null>;
  onChange: (value: string) => void;
  value: string;
  aiResponseLang?: string;
  children: React.ReactNode;
}) => {
  // Get persistent state for this editor instance
  const persistentState = getOrCreateEditorState(mdxEditorRef, value);

  // Initialize React state from persistent state
  const [isOpen, setIsOpen] = useState(persistentState.isOpen);
  const [previewValue, setPreviewValue] = useState<string | undefined>(
    persistentState.previewValue,
  );
  const [prompt, setPrompt] = useState(persistentState.prompt);
  const [loading, setLoading] = useState(persistentState.loading);
  const isCancelled = useRef(persistentState.isCancelled);
  const initialValue = useRef(persistentState.initialValue);
  const generatedValue = useRef(persistentState.generatedValue);

  // Sync React state back to persistent storage
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-way data binding
  useEffect(() => {
    persistentState.isOpen = isOpen;
  }, [isOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: one-way data binding
  useEffect(() => {
    persistentState.previewValue = previewValue;
  }, [previewValue]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: one-way data binding
  useEffect(() => {
    persistentState.prompt = prompt;
  }, [prompt]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: one-way data binding
  useEffect(() => {
    persistentState.loading = loading;
  }, [loading]);

  // Update initialValue when value prop changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: one-way data binding
  useEffect(() => {
    if (initialValue.current !== value) {
      initialValue.current = value;
      persistentState.initialValue = value;
    }
  }, [value]);

  const reset = useCallback(() => {
    setPreviewValue(undefined);
    setLoading(false);
    setPrompt("");
    setIsOpen(false);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const handleAccept = useCallback(() => {
    // Use generated value if available, otherwise current editor content
    const newValue =
      generatedValue.current.value ||
      mdxEditorRef?.current?.getMarkdown() ||
      "";
    // Apply directly — RichTextEditor skips value→editor sync while focused
    mdxEditorRef?.current?.setMarkdown(newValue);
    onChange(newValue);
    initialValue.current = newValue;
    persistentState.initialValue = newValue;
    generatedValue.current = { value: "", diff: "" };
    reset();
  }, [mdxEditorRef, onChange, reset]);

  const togglePreview = useCallback(() => {
    const md = mdxEditorRef?.current?.getMarkdown();
    if (md === initialValue.current) {
      mdxEditorRef?.current?.setMarkdown(generatedValue.current.value);
    } else {
      mdxEditorRef?.current?.setMarkdown(initialValue.current);
    }
  }, [mdxEditorRef]);

  const handleReject = useCallback(() => {
    generatedValue.current = { value: "", diff: "" };
    mdxEditorRef?.current?.setMarkdown(initialValue.current);
    reset();
  }, [mdxEditorRef, reset]);

  const contextValue: TextEnhancementContextType = useMemo(
    () => ({
      isOpen,
      setIsOpen,
      previewValue,
      setPreviewValue,
      prompt,
      setPrompt,
      loading,
      setLoading,
      isCancelled,
      initialValue,
      generatedValue,
      handleAccept,
      handleReject,
      togglePreview,
      reset,
      mdxEditorRef,
      onChange,
      aiResponseLang,
    }),
    [
      isOpen,
      previewValue,
      prompt,
      loading,
      handleAccept,
      handleReject,
      togglePreview,
      reset,
      mdxEditorRef,
      onChange,
      aiResponseLang,
    ],
  );

  return (
    <TextEnhancementContext.Provider value={contextValue}>
      {children}
    </TextEnhancementContext.Provider>
  );
};

// TextEnhancement component - the main enhancement UI
export const TextEnhancement = () => {
  const {
    prompt,
    setPrompt,
    loading,
    setLoading,
    isCancelled,
    handleReject,
    initialValue,
    generatedValue,
    setIsOpen,
    setPreviewValue,
    aiResponseLang,
  } = useTextEnhancementContext();
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const reviewOldValueRef = useRef(false);

  // Reset state when text enhancement opens
  useEffect(() => {
    reviewOldValueRef.current = false;
  }, []);

  const handleRefine = async () => {
    if (!prompt) return;

    setLoading(true);
    const parts = [
      `Give me a new version of the text, based on the following instructions: ${prompt}.\n`,
      "Only allow bold and links — only when helpful or explicitly requested.\n",
      "Only allow italic, bulleted/numbered lists — only when explicitly requested.\n",
      "Do not add any other formatting or headings.\n",
      "NEVER return objects or arrays, ALWAYS return a string.\n",
    ];

    const initial = initialValue.current;
    const generated = generatedValue.current.value;

    if (initial) parts.push(`Initial text: ${initial}\n`);
    if (initial && generated)
      parts.push(`Second version of the text: ${generated}\n`);
    if (!initial && generated) parts.push(`The text: ${generated}`);

    const systemPrompt = parts.join("");

    const lang =
      aiResponseLang?.trim() || i18n.resolvedLanguage || "en";

    await geminiRequest({
      body: systemPrompt,
      assistantId: "default",
      lang,
    }).then((res) => {
      if (isCancelled.current) return;
      const result = cleanAndParseAiJson(res || "") as string;
      console.log(result);
      if (typeof result === "string" && result !== "") {
        // Store generated value in ref for reliable toggle
        generatedValue.current = {
          value: result,
          diff: diffToMarkdown(initialValue.current, result, {
            inlineDeletes: true,
          }),
        };

        setPreviewValue(generatedValue.current.value);
        // Reset to showing generated content (not reviewing old value)
        reviewOldValueRef.current = false;
      }

      setLoading(false);
      setPrompt("");
    });
  };

  useOnClickOutside(inputRef, () => {
    if (!generatedValue.current.value) {
      setIsOpen(false);
    }
  });

  return (
    <div className="contents" ref={inputRef}>
      <Input
        classNames={{
          inputContainer:
            "w-[calc(var(--radix-popover-trigger-width)+1.5rem)] shadow-lg duration-0",
        }}
        value={prompt}
        onChange={(e) => {
          setPrompt(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleRefine();
          }
          if (e.key === "Escape") {
            if (!generatedValue.current.value) {
              e.preventDefault();
              handleReject();
            }
          }
        }}
        placeholder={
          !initialValue.current && !generatedValue.current
            ? t("global:provideDetailsCreateText")
            : t("global:provideDetailsImproveText")
        }
        onClick={(e) => {
          e.stopPropagation();
        }}
        type="textarea"
        rows={1}
        maxRows={5}
        autoFocus
        leadingIcon="colorful/ai"
        disabled={loading}
        append={
          <Button
            variant="primary"
            size="sm"
            disabled={!prompt || loading}
            leadingIcon={loading ? "loading" : "arrow-up-md"}
            classNameWrapper="-m-1 self-end -translate-y-px"
            tooltipLabel={
              !loading &&
              (!initialValue.current && !generatedValue.current
                ? t("util:generate", { val: t("global:text") })
                : t("util:improve", { val: t("global:text") }))
            }
            onClick={handleRefine}
          />
        }
      />
    </div>
  );
};

// TextEnhancementWrapper - wraps TextEnhancement in a Popover
export const TextEnhancementWrapper = ({
  dimensions,
}: {
  dimensions: { width: number; left: number; height: number };
}) => {
  const { isOpen } = useTextEnhancementContext();
  const id = useId();
  return (
    <Popover.Root
      open={isOpen}
      key={`${dimensions.left}-${dimensions.width}-${dimensions.height}`}
    >
      <Popover.Anchor />
      <Popover.Portal container={document.body}>
        <Popover.Content
          collisionPadding={16}
          sideOffset={12}
          style={{
            zIndex: Z_INDEX.RTE_POPOVER,
          }}
        >
          <style id={id}>{`
						[data-radix-popper-content-wrapper]:has(#${id}):has([data-side='top']) {
							top: auto !important;
							bottom: 0;
							transform: translate(${dimensions.left}px, -1rem) !important;
							margin-inline: -0.75rem !important;
              z-index: ${Z_INDEX.RTE_POPOVER} !important;
						}
					`}</style>
          <TextEnhancement />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

// TextEnhancementControls - control buttons for accept/reject/enhance
export const TextEnhancementControls = () => {
  const { t } = useTranslation();
  const { isOpen, prompt, handleReject, handleAccept, generatedValue } =
    useTextEnhancementContext();

  const style = useThemeColor(
    {
      color: "#9A70E1", // purple-800
    },
    { cssSelector: ".ai-enhancement" },
  );
  const ref = useRef<HTMLDivElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: generatedValue.current.value is needed to trigger the effect
  useEffect(() => {
    if (!isOpen) return;
    ref.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [isOpen, generatedValue.current.value]);
  if (!isOpen || !generatedValue.current.value) return null;
  return (
    <div
      className={cn(
        "relative pb-7 -m-2 mt-3 p-3 px-4 flex flex-col gap-3 ",
        isOpen &&
          generatedValue.current.value &&
          "ai-enhancement text-[var(--primary-700)] before:rounded-lg before:absolute before:inset-0 before:animate-pulse before:bg-[var(--primary-100)]",
      )}
    >
      <GlobalStyle custom={style} />
      <div className="flex flex-col gap-1 relative z-10">
        <div className="flex items-center gap-1 text-xs text-[var(--primary-400)]">
          <Icon name="ai" className="w-[1em]" />
          {t("global:textProposal")}
        </div>
        <Markdown className="markdown">{generatedValue.current.value}</Markdown>
      </div>
      <div className={cn("-mx-0.5 w-auto self-end flex gap-1")} ref={ref}>
        <Button
          onClick={handleReject}
          size="sm"
          text={
            t("util:discard").trim().at(0)?.toUpperCase() +
            t("util:discard").trim().slice(1)
          }
          variant="shapeless"
          className="text-[var(--primary-400)]"
          textSubdued
        />
        <Button
          onClick={handleAccept}
          variant="primary"
          size="sm"
          text={t("global:accept")}
          className={cn(
            (!!prompt || !generatedValue.current.value) && "mix-blend-multiply",
          )}
          disabled={!!prompt || !generatedValue.current.value}
        />
      </div>
    </div>
  );
};
